/**
 * Main entry point for the HKPN Vue.js application.
 * This file initializes the Vue application, configures global properties,
 * and registers custom elements from the Dataforsyningen design system.
 */
import './utils/retryFetch'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import router from '@/router'
import App from './App.vue'
import '@dataforsyningen/designsystem/assets/designsystem.css'
import { Tabs, DSNavResponsive, Spinner, DSLogoTitle, DSLogo, DSSwitch } from '@dataforsyningen/designsystem'

/**
 * Register custom elements from the Dataforsyningen design system.
 * These elements can be used directly in HTML templates.
 */
const customElementDefinitions = {
  'ds-nav-responsive': DSNavResponsive,
  'ds-switch': DSSwitch,
  'ds-tabs': Tabs,
  'ds-logo': DSLogo,
  'ds-logo-title': DSLogoTitle,
  'ds-spinner': Spinner
}

// Register all custom elements in a single loop
Object.entries(customElementDefinitions).forEach(([name, component]) => {
  customElements.define(name, component)
})

// Create the Vue application instance
const app = createApp(App)


/**
 * Configure global properties and settings for the Vue application
 * - append: Optimized utility function to safely append paths
 * - performance: Enable performance tracking in development mode
 * - $store: Make Vuex store globally available
 */
app.config.globalProperties.append = (path, pathToAppend) => `${path}${path.endsWith('/') ? '' : '/'}${pathToAppend}`
app.config.performance = import.meta.env.MODE !== 'production'
app.config.devtools = import.meta.env.VITE_VUE_PROD_DEVTOOLS

// Initialize Vue plugins (router and store)
app.use(router)
  .use(createPinia())

/**
 * Mount the application to the DOM once it's fully loaded.
 * Using capture, once, and passive options for optimal performance.
 * Includes error handling for the mount process.
 */
window.addEventListener('DOMContentLoaded', () => {
  try {
    app.mount('#app')
  } catch (error) {
    console.error('Failed to mount application:', error)
    // You might want to show a user-friendly error message here
  }
}, { capture: true, once: true, passive: true })


